import {
  InfoOutlined,
  Attribution as AttributionIcon,
  AudioFile as AudioFileIcon,
  Person as PersonIcon,
  MusicNote as MusicNoteIcon,
  AccountCircle as AccountCircleIcon,
  PlayCircle as PlayCircleIcon,
  Article as ArticleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Grid,
  Paper,
  SelectChangeEvent,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AIHost,
  AIHostResponse,
  CoHostPersonality,
  ListenerProfile,
  StudioConfig,
  ArticleSelectionItem,
} from '../../../types/studio.types';
import {
  generateNewsPreview,
  generateVoiceSample,
  JobStatus,
  StudioPreviewJob,
  mapStudioPreviewResultToGenerateNewsPreviewResponse
} from 'api/apiClient';
import {
  prebuiltEndUserProfiles
} from '../../../data/prebuiltProfiles';
import { AIGenerateHosts, AIGenerateStyle } from '../api-studio';
import AIConfirmationDialog from './common/AIConfirmationDialog';
import AudioPreview, { AudioPreviewResponse } from './common/AudioPreview';
import ListenerProfileSection from './studio/sections/ListenerProfileSection';
import MusicSection from './studio/sections/MusicSection';
import HostConfigSection from './studio/sections/HostConfigSection';
import { ListenerSettings } from './studio/sections/ListenerConfigPanel';
import EditorialStyleSection from './EditorialStyle';
import IntroductionParametersSection from './IntroductionParameters';
import CollapsibleSection from '../../../components/common/CollapsibleSection';
import ArticleSelectionPanel from './studio/sections/ArticleSelectionPanel';

// Default values for a new co-host
const defaultCoHost: CoHostPersonality = {
  name: '',
  nickname: '',
  gender: '',
  pronouns: '',
  age: '',
  background: '',
  personalityTraits: '',
  speakingStyle: '',
  voiceType: 'castingDirector',
};

// Default values for a new end-user profile
const defaultEndUserProfile: ListenerProfile = {
  name: '',
  interests: [],
  preferredContentTypes: [],
  location: '',
  age: '',
  gender: '',
  preferredPronouns: 'they/them',
  backgroundDescription: '',
  conversationalInformality: 5
};

// Default values for listener settings
const defaultListenerSettings: ListenerSettings = {
  mood: 'Neutral',
  soundscape: 'Silence',
  informality: 5
};

interface StudioTabProps {
  formData: {
    studioConfig?: StudioConfig;
    websitePK: string;
    demographics: string;
    name?: string; // NEW: provide website name to downstream components
    locationName?: string; // NEW: provide location name for IssueCast publishing
  };
  errors: Record<string, string>;
  isLoading: boolean;
  onStudioConfigChange: (config: StudioConfig) => void;
  onTestStudioConfig?: () => void;
}

interface TestConfigurationParam {
  websitePK: string;
  studioConfig: StudioConfig;
}

// NEW: Job state for async studio preview
interface StudioPreviewJobState {
  job?: StudioPreviewJob | null;
  status: JobStatus | null;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  currentStep?: string;
  stepLog?: { timestamp: string; message: string }[];
  error?: any;
}

// Include optional chapters in the param type and forward them to the API
const handleTestConfiguration = async (
  param: TestConfigurationParam & { numArticles?: number; generationMode?: 'wav' | 'hls'; chapters?: any[]; articleIds?: ArticleSelectionItem[]; podcastFormat?: 'standard' | 'emergency'; classification?: any },
  onJobStateChange?: (jobState: StudioPreviewJobState | null) => void,
): Promise<AudioPreviewResponse> => {
  console.log('Testing configuration with parameters:', { ...param, studioConfig: { /* redacted for brevity */ } });

  // Map UI podcast format to API podcast type
  const podcastType = param.podcastFormat === 'emergency' ? 'emergency' : 'multiple_articles';

  // Step 1: enqueue the job
  const enqueueResponse = await generateNewsPreview(
    param.websitePK,
    param.studioConfig,
    param.numArticles,
    param.generationMode,
    param.chapters,
    param.articleIds,
    podcastType,
    param.classification,
  );

  const { jobId } = enqueueResponse;

  if (!jobId) {
    throw new Error('Failed to enqueue studio preview job: missing jobId');
  }

  // Initialize job state immediately
  onJobStateChange?.({
    job: {
      jobId,
      type: enqueueResponse.type,
      status: enqueueResponse.status,
    } as StudioPreviewJob,
    status: enqueueResponse.status,
    createdAt: undefined,
    startedAt: undefined,
    finishedAt: undefined,
    currentStep: undefined,
    stepLog: [],
    error: undefined,
  });

  // Step 2: open SSE stream and wait for completion
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const sseUrl = `${baseUrl.replace(/\/$/, '')}/jobs/${encodeURIComponent(jobId)}/events`;

  const finalJob: StudioPreviewJob = await new Promise<StudioPreviewJob>((resolve, reject) => {
    const source = new EventSource(sseUrl, { withCredentials: true });

    source.addEventListener('job', (event: MessageEvent) => {
      try {
        const job = JSON.parse(event.data) as StudioPreviewJob;
        onJobStateChange?.({
          job,
          status: job.status,
          createdAt: job.createdAt,
          startedAt: job.startedAt,
          finishedAt: job.finishedAt,
          currentStep: job.currentStep,
          stepLog: job.stepLog,
          error: job.error,
        });
      } catch (e) {
        console.error('Failed to parse job event data:', e);
      }
    });

    source.addEventListener('complete', (event: MessageEvent) => {
      try {
        const job = JSON.parse(event.data) as StudioPreviewJob;
        onJobStateChange?.({
          job,
          status: job.status,
          createdAt: job.createdAt,
          startedAt: job.startedAt,
          finishedAt: job.finishedAt,
          currentStep: job.currentStep,
          stepLog: job.stepLog,
          error: job.error,
        });

        source.close();

        if (job.status === 'succeeded' && job.result) {
          resolve(job);
        } else {
          reject(job.error || new Error(`Studio preview job did not succeed (status: ${job.status})`));
        }
      } catch (e) {
        source.close();
        reject(e);
      }
    });

    source.addEventListener('error', (event: MessageEvent) => {
      console.error('SSE error event for studio preview job:', event);
      try {
        const payload = (event as any).data ? JSON.parse((event as any).data) : null;
        onJobStateChange?.({
          job: undefined,
          status: 'failed',
          error: payload || event,
        });
      } catch {
        onJobStateChange?.({ job: undefined, status: 'failed', error: event });
      }
      source.close();
      reject(new Error('Connection to preview job stream was lost.'));
    });
  });

  if (!finalJob.result) {
    throw new Error('Studio preview job succeeded but result payload is missing.');
  }

  const previewResponse = mapStudioPreviewResultToGenerateNewsPreviewResponse(
    finalJob.result,
    undefined,
    param.generationMode || 'hls',
  );

  let scriptData: any = undefined;
  if (previewResponse) {
    const hasStructuredScript = (previewResponse as any).newsScript && (typeof (previewResponse as any).newsScript === 'object');
    const hasMetadata = (previewResponse as any).metadata != null;

    if (hasStructuredScript) {
      const newsScript = (previewResponse as any).newsScript;
      if (newsScript && (newsScript.script || newsScript.metadata)) {
        scriptData = newsScript;
      } else {
        scriptData = {
          metadata: (previewResponse as any).metadata ?? null,
          script: (previewResponse as any).newsScript ?? null,
        };
      }
    } else if (hasMetadata) {
      scriptData = {
        metadata: (previewResponse as any).metadata,
        script: undefined,
      };
    }
  }

  const isHls = !!previewResponse.hlsUrl;
  const classification = (finalJob.result as any)?.metadata?.classification;

  return {
    hlsUrl: previewResponse.hlsUrl,
    audioData: isHls ? undefined : previewResponse.audio,
    description: isHls
      ? 'Live streaming news segment generated from current studio configuration.'
      : 'This fully polished news segment is generated based on your studio configuration.',
    headers: previewResponse.headers,
    scriptData,
    articleIds: (previewResponse as any).articleIds,
    chapters: (previewResponse as any).chapters,
    classification,
  };
};

const StudioTab: React.FC<StudioTabProps> = ({
  formData,
  errors,
  isLoading,
  onStudioConfigChange,
  onTestStudioConfig,
}) => {
  console.log('DEBUG: StudioTab render. formData:', formData);
  console.log('DEBUG: StudioTab websitePK:', formData?.websitePK);

  // Initialize studioConfig if it doesn't exist
  const studioConfig = formData.studioConfig || {
    format: 'cohost',
    hostIdentification: 'named',
    coHosts: [{ ...defaultCoHost }, { ...defaultCoHost }],
    newsAttribution: '',
    sponsor: '',
    premiumUpsellOffer: '',
    editorialStyle: '',
    introMusic: '',
    outroMusic: '',
    endUserProfile: { ...defaultEndUserProfile },
    relationshipDynamics: '', // For co-host format
    listenerSettings: { ...defaultListenerSettings },
  };

  const [isAiGeneratingEditorialStyle, setIsAiGeneratingEditorialStyle] = useState(false);
  const [isAiGeneratingHostPersonalities, setIsAiGeneratingHostPersonalities] = useState(false);

  // Track live listener settings locally to avoid async prop propagation timing
  const [liveListenerSettings, setLiveListenerSettings] = useState<ListenerSettings>(
    // Try to seed from any existing values; otherwise default
    (studioConfig as any).listenerSettings || (studioConfig.endUserProfile as any)?.listenerSettings || {
      mood: 'Neutral',
      soundscape: 'Silence',
      informality: 5,
    }
  );

  // For timer
  // For Editorial Style
  const [editorialElapsedTime, setEditorialElapsedTime] = useState<number>(0);
  const [editorialTimerInterval, setEditorialTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [editorialGenerationComplete, setEditorialGenerationComplete] = useState<boolean>(false);

  // For Host Personalities
  const [hostElapsedTime, setHostElapsedTime] = useState<number>(0);
  const [hostTimerInterval, setHostTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [hostGenerationComplete, setHostGenerationComplete] = useState<boolean>(false);

  // State for co-host tab selection
  const [coHostTabValue, setCoHostTabValue] = useState(0);

  // State for AI dialog
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiHostDialogOpen, setHostAiDialogOpen] = useState(false);

  // State for pre-built profile selections
  const [/* selectedCoHostProfileIds */, setSelectedCoHostProfileIds] = useState<string[]>(
    studioConfig.coHosts.map(() => '')
  );
  const [selectedEndUserProfileId, setSelectedEndUserProfileId] = useState<string>('');

  // State for number of articles (persisted per website)
  const [numArticles, setNumArticles] = useState(3); // Default to 3 articles
  const [generationMode, setGenerationMode] = useState<'wav' | 'hls'>('hls');
  const [podcastFormat, setPodcastFormat] = useState<'standard' | 'emergency'>('standard');
  
  // NEW: State for selected article IDs
  const [selectedArticleItems, setSelectedArticleItems] = useState<ArticleSelectionItem[]>([]);

  // NEW: Store the text used for the most recent voice sample
  const [lastVoiceText, setLastVoiceText] = useState<string | null>(null);
  const [lastVoiceTextHost, setLastVoiceTextHost] = useState<string>('');

  // NEW: Studio preview job tracking state
  const [studioJobState, setStudioJobState] = useState<StudioPreviewJobState | null>(null);
  const [studioJobExpanded, setStudioJobExpanded] = useState<boolean>(true);
  const [studioJobElapsedSeconds, setStudioJobElapsedSeconds] = useState<number>(0);

  // Track the last completed job summary so we can show a collapsed re-open button
  const lastCompletedJobRef = useRef<StudioPreviewJobState | null>(null);

  // Timer for elapsed time while job is running
  useEffect(() => {
    const job = studioJobState?.job;
    if (!job || !job.createdAt) {
      setStudioJobElapsedSeconds(0);
      return;
    }

    const createdAtMs = Date.parse(job.createdAt);
    if (Number.isNaN(createdAtMs)) {
      setStudioJobElapsedSeconds(0);
      return;
    }

    // Initialize immediately
    setStudioJobElapsedSeconds(Math.max(0, Math.floor((Date.now() - createdAtMs) / 1000)));

    if (studioJobState.status === 'pending' || studioJobState.status === 'running') {
      const interval = setInterval(() => {
        setStudioJobElapsedSeconds(Math.max(0, Math.floor((Date.now() - createdAtMs) / 1000)));
      }, 1000);
      return () => clearInterval(interval);
    }

    return undefined;
  }, [studioJobState]);

  // Capture last completed job when the current job transitions to a terminal state
  useEffect(() => {
    if (!studioJobState || !studioJobState.status) return;

    if (
      studioJobState.status === 'succeeded' ||
      studioJobState.status === 'failed' ||
      studioJobState.status === 'canceled'
    ) {
      lastCompletedJobRef.current = studioJobState;
      // Auto-collapse the panel once job has finished
      setStudioJobExpanded(false);
    }
  }, [studioJobState]);

  const formatLocalTime = (iso?: string): string | null => {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
  };

  const formatDuration = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0s';
    const s = Math.floor(seconds % 60);
    const m = Math.floor((seconds / 60) % 60);
    const h = Math.floor(seconds / 3600);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const currentJobDisplay = useMemo(() => {
    return studioJobState && studioJobState.job ? studioJobState : lastCompletedJobRef.current;
  }, [studioJobState]);

  const renderStudioJobPanel = () => {
    if (!currentJobDisplay) return null;

    const job = currentJobDisplay.job;
    const status = currentJobDisplay.status;
    const isActive = status === 'pending' || status === 'running';
    const createdLocal = formatLocalTime(job?.createdAt);

    const severity = status === 'failed' ? 'error' : status === 'succeeded' ? 'success' : 'info';

    if (!studioJobExpanded) {
      return (
        <Box sx={{ mb: 2 }}>
          <Alert severity={severity as any} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Last studio preview job {status === 'succeeded' ? 'completed' : status}
              </Typography>
              {createdLocal && (
                <Typography variant="caption" color="text.secondary">
                  Started at {createdLocal}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                component="button"
                sx={{
                  ml: 2,
                  border: 'none',
                  background: 'transparent',
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.8rem',
                }}
                onClick={() => setStudioJobExpanded(true)}
              >
                View details
              </Typography>
            </Box>
          </Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity={severity as any}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Studio preview job {job?.jobId ? `(${job.jobId.slice(0, 8)}...)` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {status}
                {currentJobDisplay.currentStep ? ` • Step: ${currentJobDisplay.currentStep}` : ''}
              </Typography>
            </Box>
            <Typography
              component="button"
              sx={{
                ml: 2,
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.8rem',
              }}
              onClick={() => setStudioJobExpanded(false)}
            >
              Hide
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            {createdLocal && (
              <Typography variant="body2">
                Started at: <strong>{createdLocal}</strong>
              </Typography>
            )}
            <Typography variant="body2">
              Elapsed time: <strong>{formatDuration(studioJobElapsedSeconds)}</strong>
            </Typography>
          </Box>

          {currentJobDisplay.stepLog && currentJobDisplay.stepLog.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 160, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Job steps
              </Typography>
              {currentJobDisplay.stepLog.map((entry, idx) => (
                <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace' }}>
                  [{formatLocalTime(entry.timestamp) || entry.timestamp}] {entry.message}
                </Typography>
              ))}
            </Box>
          )}

          {status === 'failed' && currentJobDisplay.error && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Error details
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {currentJobDisplay.error.message || JSON.stringify(currentJobDisplay.error, null, 2)}
              </Typography>
            </Box>
          )}

          {isActive && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              You can navigate within this page while the job runs. This panel updates automatically.
            </Typography>
          )}
        </Alert>
      </Box>
    );
  };

  // Load persisted settings when website changes
  useEffect(() => {
    try {
      const baseKey = `studioTab:${formData.websitePK}`;
      const savedArticles = localStorage.getItem(`${baseKey}:numArticles`);
      if (savedArticles !== null && !isNaN(Number(savedArticles))) {
        setNumArticles(Number(savedArticles));
      }
      const savedMode = localStorage.getItem(`${baseKey}:generationMode`);
      if (savedMode === 'wav' || savedMode === 'hls') {
        setGenerationMode(savedMode);
      }
      const savedFormat = localStorage.getItem(`${baseKey}:podcastFormat`);
      if (savedFormat === 'standard' || savedFormat === 'emergency') {
        setPodcastFormat(savedFormat);
      }
      // Load persisted article selection
      const savedArticleIds = localStorage.getItem(`${baseKey}:articleIds`);
      if (savedArticleIds) {
        try {
          const parsed = JSON.parse(savedArticleIds);
          if (Array.isArray(parsed)) {
            // Migration: if it's an array of strings, convert to objects
            if (parsed.length > 0 && typeof parsed[0] === 'string') {
               setSelectedArticleItems(parsed.map((id: string) => ({ articleId: id })));
            } else {
               setSelectedArticleItems(parsed);
            }
          }
        } catch (e) {
          console.warn('Failed parsing saved articleIds', e);
        }
      }
    } catch (e) {
      console.warn('Failed loading persisted studioTab settings', e);
    }
  }, [formData.websitePK]);

  const persistGenerationMode = (mode: 'wav' | 'hls') => {
    setGenerationMode(mode);
    try {
      localStorage.setItem(`studioTab:${formData.websitePK}:generationMode`, mode);
    } catch (e) {
      console.warn('Failed saving generationMode', e);
    }
  };

  const persistPodcastFormat = (format: 'standard' | 'emergency') => {
    setPodcastFormat(format);
    try {
      localStorage.setItem(`studioTab:${formData.websitePK}:podcastFormat`, format);
    } catch (e) {
      console.warn('Failed saving podcastFormat', e);
    }
  };

  const persistArticleItems = (items: ArticleSelectionItem[]) => {
    setSelectedArticleItems(items);
    try {
      localStorage.setItem(`studioTab:${formData.websitePK}:articleIds`, JSON.stringify(items));
    } catch (e) {
      console.warn('Failed saving articleIds', e);
    }
  };

  useEffect(() => {
    return () => {
      if (editorialTimerInterval) {
        clearInterval(editorialTimerInterval);
      }
      if (hostTimerInterval) {
        clearInterval(hostTimerInterval);
      }
    };
  }, [editorialTimerInterval, hostTimerInterval]);

  // Handle format change (solo vs co-host)
  const handleFormatChange = (newFormat: 'cohost' | 'solo') => {
    if (newFormat === 'cohost') {
      // If switching to cohost and only one co-host exists, add a second default co-host
      let updatedCoHosts = studioConfig.coHosts;
      if (updatedCoHosts.length === 1) {
        updatedCoHosts = [
          { ...updatedCoHosts[0] },
          { ...defaultCoHost }
        ];
      }
      onStudioConfigChange({
        ...studioConfig,
        format: newFormat,
        coHosts: updatedCoHosts,
      });
    } else if (newFormat === 'solo') {
      // If switching to solo, keep only the first co-host
      onStudioConfigChange({
        ...studioConfig,
        format: newFormat,
        coHosts: studioConfig.coHosts.length > 0 ? [{ ...studioConfig.coHosts[0] }] : [{ ...defaultCoHost }],
      });
    } else {
      onStudioConfigChange({
        ...studioConfig,
        format: newFormat,
      });
    }
  };

  // Handle text input changes
  const handleTextChange = (field: string, value: string | string[]) => {
    onStudioConfigChange({
      ...studioConfig,
      [field]: value,
    });
  };

  const handleAIHostChange = (response: AIHostResponse) => {
    // If response is a string, assume it is JSON and parse it into an object
    if (typeof response === 'string') {
      response = JSON.parse(response);
    }
    console.log('AI Generated Host Personalities Response:', response);

    if (!response || !response.hosts || !Array.isArray(response.hosts)) {
      console.error('Invalid response format from AI host generation');
      return;
    }

    // Determine format from response
    const format = response.formatType === 'co-hosted' ? 'cohost' : 'solo';

    // Map hosts from response to the format expected by the form
    const coHosts = response.hosts.map((host: AIHost) => {
      // Combine racial background and detailed history into background field
      const background = `${host.racialBackgroundAndAccent}\n\n${host.backgroundHistoryAndInterests}`;

      return {
        name: host.fullName || '',
        nickname: host.nickname || '',
        gender: host.gender || '',
        pronouns: host.pronouns || '',
        age: String(host.age) || '',
        background: background || '',
        personalityTraits: host.personalityTraits || '',
        speakingStyle: host.speakingStyle || '',
        voiceType: 'castingDirector' as 'castingDirector' | 'journalist' | 'custom', // Type assertion to match CoHostPersonality
      };
    });

    // Update the studioConfig with the new data
    onStudioConfigChange({
      ...studioConfig,
      format: format,
      coHosts: coHosts,
      relationshipDynamics: response.relationshipDynamics || '',
      // Maintain other properties that shouldn't be changed
      newsAttribution: studioConfig.newsAttribution,
      editorialStyle: studioConfig.editorialStyle,
      introMusic: studioConfig.introMusic,
      outroMusic: studioConfig.outroMusic,
      endUserProfile: studioConfig.endUserProfile,
    });

    // Reset the selected profile IDs
    setSelectedCoHostProfileIds(coHosts.map(() => ''));

    // Update the tab index if needed
    if (coHostTabValue >= coHosts.length) {
      setCoHostTabValue(0);
    }

    // If the format changed, make sure there's at least one host
    if (format === 'solo' && coHosts.length === 0) {
      onStudioConfigChange({
        ...studioConfig,
        format: format,
        coHosts: [{ ...defaultCoHost }],
      });
    }
  };

  // Handle end-user profile changes
  const handleEndUserProfileChange = (field: keyof ListenerProfile, value: any) => {
    onStudioConfigChange({
      ...studioConfig,
      endUserProfile: {
        ...studioConfig.endUserProfile,
        [field]: value,
      },
    });
  };


  // Handle AI button click
  const handleEditorialStyleButtonClick = () => {
    setAiDialogOpen(true);
  };

  const handleHostAIButtonClick = () => {
    setHostAiDialogOpen(true);
  };

  const handleGenerateHostProfile = () => {
    setHostAiDialogOpen(false);
    setIsAiGeneratingHostPersonalities(true);
    setHostGenerationComplete(false);

    // Start the timer
    const start = Date.now();
    setHostElapsedTime(0);

    // Create an interval to update elapsed time every second
    const interval = setInterval(() => {
      setHostElapsedTime(Math.floor((Date.now() - start) / 1000));
    }, 1000);

    setHostTimerInterval(interval);

    if (formData.studioConfig == null || formData.studioConfig?.editorialStyle == null) {
      return;
    }

    AIGenerateHosts(formData.websitePK,
      formData.studioConfig.editorialStyle,
      formData.demographics,
      formData.studioConfig.format,
    ).then((response) => {
      // Stop the timer
      if (hostTimerInterval) {
        clearInterval(hostTimerInterval);
        setHostTimerInterval(null);
      }

      // Calculate final time
      const totalTime = Math.floor((Date.now() - start) / 1000);
      setHostElapsedTime(totalTime);

      if (response.error) {
        alert(`Failed to generate hosts: ${response.error}`);
        setIsAiGeneratingHostPersonalities(false);
        setHostGenerationComplete(false); // Or true depending on desired UX, but likely false if failed
        return;
      }

      // Update UI state
      handleAIHostChange(response);
      setIsAiGeneratingHostPersonalities(false);
      setHostGenerationComplete(true);
    }).catch((error) => {
       // Stop the timer
       if (hostTimerInterval) {
        clearInterval(hostTimerInterval);
        setHostTimerInterval(null);
      }
      console.error("Error generating hosts:", error);
      alert("Failed to generate hosts. Please try again.");
      setIsAiGeneratingHostPersonalities(false);
    });
  }

  const handleAIConfirmation = () => {
    setAiDialogOpen(false);
    setIsAiGeneratingEditorialStyle(true);
    setEditorialGenerationComplete(false);

    // Start the timer
    const start = Date.now();
    setEditorialElapsedTime(0);

    // Create an interval to update elapsed time every second
    const interval = setInterval(() => {
      setEditorialElapsedTime(Math.floor((Date.now() - start) / 1000));
    }, 1000);

    setEditorialTimerInterval(interval);

    AIGenerateStyle(formData.websitePK, formData.studioConfig?.format ?? "cohost").then((response) => {
      // If response is JSON, convert it to a string
      if (typeof response === 'object' && response !== null && !response.error) {
        response = JSON.stringify(response, null, 2);
      }

      // Stop the timer
      if (editorialTimerInterval) {
        clearInterval(editorialTimerInterval);
        setEditorialTimerInterval(null);
      }

      // Calculate final time
      const totalTime = Math.floor((Date.now() - start) / 1000);
      setEditorialElapsedTime(totalTime);

      if (response.error) {
        alert(`Failed to generate editorial style: ${response.error}`);
        setIsAiGeneratingEditorialStyle(false);
        setEditorialGenerationComplete(false);
        return;
      }

      // Update UI state
      handleTextChange('editorialStyle', response);
      setIsAiGeneratingEditorialStyle(false);
      setEditorialGenerationComplete(true);
    }).catch((error) => {
       // Stop the timer
       if (editorialTimerInterval) {
        clearInterval(editorialTimerInterval);
        setEditorialTimerInterval(null);
      }
      console.error("Error generating editorial style:", error);
      alert("Failed to generate editorial style. Please try again.");
      setIsAiGeneratingEditorialStyle(false);
    });
  };
  // Handle dialog close
  const handleDialogClose = () => {
    setAiDialogOpen(false);
  };

  // Handle dialog close
  const handlHosteDialogClose = () => {
    setHostAiDialogOpen(false);
  };

  // Handle selecting a pre-built end-user profile
  const handleSelectPrebuiltEndUser = (event: SelectChangeEvent<string>) => {
    const profileId = event.target.value;
    setSelectedEndUserProfileId(profileId);

    if (!profileId) return;

    const selectedProfile = prebuiltEndUserProfiles.find(p => p.id === profileId);
    if (!selectedProfile || !selectedProfile.endUserProfile) return;

    onStudioConfigChange({
      ...studioConfig,
      endUserProfile: {
        ...selectedProfile.endUserProfile,
      },
    });
  };

  const handlePlayVoiceSample = async (
    payload: CoHostPersonality | { host: CoHostPersonality; voiceText?: string }
  ): Promise<AudioPreviewResponse> => {
    const errorResponse: AudioPreviewResponse = {
      audioData: null,
      description: 'Error playing voice sample.'
    }

    try {
      const host = (payload && typeof payload === 'object' && 'name' in payload && !('host' in payload))
        ? (payload as CoHostPersonality)
        : (payload as { host: CoHostPersonality; voiceText?: string }).host;

      const overrideVoiceText = (typeof payload === 'object' && 'host' in payload)
        ? (payload as { host: CoHostPersonality; voiceText?: string }).voiceText
        : undefined;

      const sampleText = overrideVoiceText && overrideVoiceText.trim().length > 0
        ? overrideVoiceText
        : (host.castingDirectorExplanation ?? `Hello, I'm ${host.name}, and people call me ${host.nickname}`);

      const { audio, voiceId, voiceText, headers } = await generateVoiceSample(formData.websitePK, host, sampleText);

      // Only update the host profile with the voiceId if the host doesn't already have one
      if (!host.voiceId && voiceId) {
        const updatedCoHosts = studioConfig.coHosts.map((h) =>
          h === host ? { ...h, voiceId } : h
        );

        onStudioConfigChange({
          ...studioConfig,
          coHosts: updatedCoHosts,
        });
      }

      // Track the last voice text shown
      const effectiveVoiceText = (typeof voiceText === 'string' && voiceText.trim().length > 0) ? voiceText : sampleText;
      setLastVoiceTextHost(host.name || host.nickname || 'Host');
      setLastVoiceText(effectiveVoiceText);

      return audio ? { audioData: audio, description: `Audio sample for ${host.name}`, headers, voiceText: effectiveVoiceText } : errorResponse;
    } catch (error) {
      console.error("Error playing voice sample:", error);
      alert("Error playing voice sample. Please try again.");
      return errorResponse;
    }
  }

  // Compute a preview config that always mirrors the slider value into endUserProfile.conversationalInformality
  const previewStudioConfig: StudioConfig = {
    ...studioConfig,
    endUserProfile: {
      ...studioConfig.endUserProfile,
      conversationalInformality:
        // Prefer the live slider value captured locally; then fall back to stored profile; then default
        liveListenerSettings?.informality ?? studioConfig.endUserProfile?.conversationalInformality ?? 5,
    },
  } as StudioConfig;

  // Status validation helpers for each section
  const getEditorialStyleStatus = (): { state: 'valid' | 'warning' | 'error'; missingFields: string[] } => {
    // Important field: Editorial Style
    const hasEditorialStyle = !!(studioConfig.editorialStyle && studioConfig.editorialStyle.trim().length > 0);
    
    if (!hasEditorialStyle) {
      return { state: 'warning', missingFields: ['Speech & Audio Style Guide'] };
    }
    
    return { state: 'valid', missingFields: [] };
  };

  const getHostConfigStatus = (): { state: 'valid' | 'warning' | 'error'; missingFields: string[] } => {
    // Determine number of required co-hosts based on format
    const numRequiredHosts = studioConfig.format === 'solo' ? 1 : 2;
    const coHosts = studioConfig.coHosts || [];

    const missingFields: string[] = [];

    // Check required fields for each co-host
    for (let i = 0; i < numRequiredHosts; i++) {
      const host = coHosts[i];
      const hostLabel = studioConfig.format === 'solo' ? 'Host' : `Co-Host ${i + 1}`;
      
      if (!host) {
        missingFields.push(`${hostLabel}: All fields`);
        continue;
      }

      // Required fields: Full Name, Nickname, Gender, Age, Personality Traits, Speaking Style
      if (!host.name || !host.name.trim()) missingFields.push(`${hostLabel}: Full Name`);
      if (!host.nickname || !host.nickname.trim()) missingFields.push(`${hostLabel}: Nickname`);
      if (!host.gender || !host.gender.trim()) missingFields.push(`${hostLabel}: Gender`);
      if (!host.age || !host.age.trim()) missingFields.push(`${hostLabel}: Age`);
      if (!host.personalityTraits || !host.personalityTraits.trim()) missingFields.push(`${hostLabel}: Personality Traits`);
      if (!host.speakingStyle || !host.speakingStyle.trim()) missingFields.push(`${hostLabel}: Speaking Style`);
      
      // Voice validation depends on voiceType:
      // - castingDirector: requires voiceId (generated by AI)
      // - journalist: no voiceId required (uses journalist's actual voice)
      // - custom: requires customVoiceId (user-provided)
      if (host.voiceType === 'castingDirector') {
        if (!host.voiceId || !host.voiceId.trim()) {
          missingFields.push(`${hostLabel}: Voice (generate a voice sample)`);
        }
      } else if (host.voiceType === 'custom') {
        if (!host.customVoiceId || !host.customVoiceId.trim()) {
          missingFields.push(`${hostLabel}: Custom Voice ID`);
        }
      }
      // journalist voice type requires no voice ID
    }

    if (missingFields.length > 0) {
      return { state: 'error', missingFields };
    }

    return { state: 'valid', missingFields: [] };
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
            Configure the studio settings for this website, including host personalities, editorial style, and music elements.
            These settings give you control over how your news segments are produced.
          </Typography>
        </Alert>
      </Grid>

      {/* Introduction Parameters Section */}
      <Grid item xs={12}>
        <IntroductionParametersSection
          newsAttribution={studioConfig.newsAttribution}
          sponsor={studioConfig.sponsor || ''}
          premiumUpsellOffer={studioConfig.premiumUpsellOffer || ''}
          isLoading={isLoading}
          onChange={(field, value) => handleTextChange(field, value)}
          icon={<AttributionIcon sx={{ mr: 1 }} />}
        />
      </Grid>

      {/* Editorial Style Section */}
      <Grid item xs={12}>
        <EditorialStyleSection
          editorialStyle={studioConfig.editorialStyle}
          errors={errors}
          isLoading={isLoading}
          isAiGeneratingEditorialStyle={isAiGeneratingEditorialStyle}
          editorialElapsedTime={editorialElapsedTime}
          editorialGenerationComplete={editorialGenerationComplete}
          onEditorialStyleChange={(value) => handleTextChange('editorialStyle', value)}
          onAIButtonClick={handleEditorialStyleButtonClick}
          onGenerationCompleteClose={() => setEditorialGenerationComplete(false)}
          icon={<AudioFileIcon sx={{ mr: 1 }} />}
          status={{ 
            enabled: true, 
            state: getEditorialStyleStatus().state,
            missingFields: getEditorialStyleStatus().missingFields
          }}
        />
      </Grid>

      {/* Format Selection */}

      {/* Co-Host Configuration Section */}
      <Grid item xs={12}>
        <HostConfigSection
          studioConfig={studioConfig}
          websitePK={formData.websitePK}
          errors={errors}
          isLoading={isLoading}
          isAiGeneratingHostPersonalities={isAiGeneratingHostPersonalities}
          hostElapsedTime={hostElapsedTime}
          hostGenerationComplete={hostGenerationComplete}
          onHostAIButtonClick={handleHostAIButtonClick}
          onHostGenerationCompleteClose={() => setHostGenerationComplete(false)}
          onStudioConfigChange={onStudioConfigChange}
          onPlayVoiceSample={handlePlayVoiceSample}
          onHandleFormatChange={handleFormatChange}
          icon={<PersonIcon sx={{ mr: 1 }} />}
          status={{ 
            enabled: true, 
            state: getHostConfigStatus().state,
            missingFields: getHostConfigStatus().missingFields
          }}
        />
      </Grid>

      {/* NEW: Display the text used for the last generated voice sample */}
      {lastVoiceText && (
        <Grid item xs={12}>
          <Paper elevation={0} variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom>
              Voice sample text{lastVoiceTextHost ? ` for ${lastVoiceTextHost}` : ''}
            </Typography>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              This is the exact text that was converted to speech. It contains ElevenLabs Audio tags for testing pacing, pauses, and emphasis.
            </Alert>
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                p: 2,
                bgcolor: 'background.paper',
                border: theme => `1px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto'
              }}
            >
              {lastVoiceText}
            </Box>
          </Paper>
        </Grid>
      )}

      {/* AI Dialog Confirmation */}
      <AIConfirmationDialog
        open={aiDialogOpen}
        title="Generate Editorial Style with AI"
        description="This operation will use AI to generate an audio style guide based on your website's content and configuration. The process may take several minutes to complete. You can wait or return later to see the results."
        onClose={handleDialogClose}
        onGenerate={handleAIConfirmation}
      />

      <AIConfirmationDialog
        open={aiHostDialogOpen}
        title="Generate Host Personalities with AI"
        description="This operation will use AI to generate your host personalities  based on your website's Audio Style Guide and demographics. The process may take several minutes to complete. You can wait or return later to see the results."
        onClose={handlHosteDialogClose}
        onGenerate={handleGenerateHostProfile}
      />

      {/* Audio Elements Section */}
      <Grid item xs={12}>
        <MusicSection
          studioConfig={studioConfig}
          isLoading={isLoading}
          onTextChange={(field, value) => handleTextChange(field, value)}
          icon={<MusicNoteIcon sx={{ mr: 1 }} />}
          websiteId={formData.websitePK}
        />
      </Grid>

      {/* Listener Profile Section */}
      <Grid item xs={12}>
        <ListenerProfileSection
          endUserProfile={studioConfig.endUserProfile}
          selectedEndUserProfileId={selectedEndUserProfileId}
          isLoading={isLoading}
          onProfileChange={handleEndUserProfileChange}
          onSelectPrebuiltProfile={handleSelectPrebuiltEndUser}
          icon={<AccountCircleIcon sx={{ mr: 1 }} />}
          namespace="studio-tab"
          // Keep the slider controlled from StudioTab and capture changes immediately
          listenerSettings={liveListenerSettings}
          onListenerSettingsChange={setLiveListenerSettings}
        />
      </Grid>

      {/* Generate news sample Section */}
      <Grid item xs={12}>
        <CollapsibleSection
          title="Generate Podcast"
          icon={<PlayCircleIcon sx={{ mr: 1 }} />}
          namespace="studio-tab"
          id="preview-news-segment"
          paperProps={{
            sx: podcastFormat === 'emergency' ? {
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : '#ffebee',
              borderColor: 'error.light'
            } : undefined
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a fully polished news podcast using all your current (even unsaved) settings.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="podcast-format"
                value={podcastFormat}
                onChange={(e) => persistPodcastFormat(e.target.value as 'standard' | 'emergency')}
              >
                <FormControlLabel
                  value="standard"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ArticleIcon sx={{ mr: 1 }} />
                      Multiple Articles
                    </Box>
                  }
                  sx={{ mr: 3 }}
                />
                <FormControlLabel
                  value="emergency"
                  control={<Radio color="warning" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
                      Emergency
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Article Selection Panel */}
          <ArticleSelectionPanel
            websitePK={formData.websitePK}
            selectedItems={selectedArticleItems}
            onSelectionChange={(items: ArticleSelectionItem[]) => {
              persistArticleItems(items);
            }}
          />

          {/* Generate button with output mode to the right */}
          <AudioPreview
            buttonTitle={`Generate full news podcast`}
            clickedButtonTitle={`Generating sample news podcast. This will take a few minutes...`}
            handleClickParam={{
              studioConfig: previewStudioConfig,
              websitePK: formData.websitePK,
              numArticles,
              generationMode,
              articleIds: selectedArticleItems.length > 0 ? selectedArticleItems : undefined,
              podcastFormat,
            }}
            handleClick={(param: any) => handleTestConfiguration(param, setStudioJobState)}
            heading={`Fully polished news podcast`}
            headingIcon={<PlayCircleIcon />}
            disabled={isLoading}
            helperText={"Generate a fully polished news podcast using all your current (even unsaved) settings."}
            websiteId={formData.websitePK?.replace('WEBSITE#', '')}
            websiteName={formData.name}
            locationName={formData.locationName}
            rightAdornment={
              <Box>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="generation-mode"
                    value={generationMode}
                    onChange={(e) => persistGenerationMode(e.target.value as 'wav' | 'hls')}
                  >
                    <FormControlLabel value="hls" control={<Radio size="small" />} label="HLS Stream" />
                    <FormControlLabel value="wav" control={<Radio size="small" />} label="WAV Preview" />
                  </RadioGroup>
                </FormControl>
                {generationMode === 'wav' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      WAV Preview is faster but you can't publish an IssueCast from a WAV.
                    </Typography>
                  </Alert>
                )}
              </Box>
            }
            // NEW: show the current job step text in the loading area
            currentJobStep={currentJobDisplay?.currentStep}
          />

          {/* NEW: Move the job status panel to just under the Generate button */}
          <Box sx={{ mt: 2 }}>
            {renderStudioJobPanel()}
          </Box>
        </CollapsibleSection>
      </Grid>
    </Grid>
  );
};

export default StudioTab;